import React from 'react';
import { Box, Chip } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type { DynamicEntityRow } from '../api/strapiDynamicEntity';
import { ChemInline } from './ChemInline';

function rowString(row: DynamicEntityRow, key: string): string {
  return String(row[key] ?? '').trim();
}

function parameterUjiFromMetode(row: DynamicEntityRow | null | undefined): {
  nama: string;
  simbol: string;
} {
  const pu = row?.parameter_uji;
  if (!pu || typeof pu !== 'object' || Array.isArray(pu)) return { nama: '', simbol: '' };
  const rec = pu as Record<string, unknown>;
  return {
    nama: String(rec.nama_parameter ?? '').trim(),
    simbol: String(rec.simbol ?? '').trim(),
  };
}

/** Plain text label (filter / a11y / sorting). */
export function metodaSamplingPlainLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  const { nama, simbol } = parameterUjiFromMetode(row);
  const puLabel = [nama, simbol].filter(Boolean).join(' / ');
  return [rowString(row, 'kode_sni'), puLabel, rowString(row, 'satuan')].filter(Boolean).join(' — ') || row.documentId;
}

/** Label dengan format kimia (KaTeX) seperti nama parameter uji. */
export function MetodaSamplingChemLabel({ row }: { row: DynamicEntityRow }) {
  const kode = rowString(row, 'kode_sni');
  const satuan = rowString(row, 'satuan');
  const { nama, simbol } = parameterUjiFromMetode(row);

  const segments: React.ReactNode[] = [];
  if (kode) segments.push(<span key="kode">{kode}</span>);
  if (nama || simbol) {
    segments.push(
      <span key="pu">
        {nama ? <ChemInline text={nama} /> : null}
        {nama && simbol ? ' (' : null}
        {simbol ? <ChemInline text={simbol} /> : null}
        {nama && simbol ? ')' : null}
      </span>,
    );
  }
  if (satuan) segments.push(<span key="satuan">{satuan}</span>);

  if (!segments.length) {
    return <>{row.documentId || '—'}</>;
  }

  return (
    <Box
      component="span"
      sx={{
        display: 'inline',
        wordBreak: 'break-word',
        '& .katex-html': { whiteSpace: 'normal' },
      }}
    >
      {segments.map((node, i) => (
        <React.Fragment key={i}>
          {i > 0 ? ' — ' : null}
          {node}
        </React.Fragment>
      ))}
    </Box>
  );
}

type MetodaSamplingMultiAutocompleteProps = {
  label?: string;
  options: DynamicEntityRow[];
  value: DynamicEntityRow[];
  onChange: (rows: DynamicEntityRow[]) => void;
  loading?: boolean;
  disabled?: boolean;
};

/** Multi-select metode sampling dengan label kimia di opsi & chip. */
export function MetodaSamplingMultiAutocomplete({
  label = 'Metode sampling',
  options,
  value,
  onChange,
  loading = false,
  disabled = false,
}: MetodaSamplingMultiAutocompleteProps) {
  return (
    <Autocomplete<DynamicEntityRow, true, false, false>
      multiple
      options={options}
      value={value}
      disabled={disabled}
      onChange={(_, v) => onChange(v)}
      loading={loading}
      getOptionLabel={(o) => metodaSamplingPlainLabel(o)}
      isOptionEqualToValue={(a, b) => a.documentId === b.documentId}
      renderOption={(props, option) => (
        <li {...props} key={option.documentId}>
          <MetodaSamplingChemLabel row={option} />
        </li>
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              {...tagProps}
              key={key}
              size="small"
              label={<MetodaSamplingChemLabel row={option} />}
            />
          );
        })
      }
      renderInput={(params) => <TextField {...params} label={label} size="small" fullWidth />}
    />
  );
}
